import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { InternalTaskBoard } from './InternalTaskBoard';
import { CampaignManager } from './CampaignManager';
import { Users, ListTodo, Megaphone } from 'lucide-react';

export function MarketingTeamLeaderDashboard() {
  const { user } = useAuth();

  const { data: myTeam } = useQuery({
    queryKey: ['my-led-mkt-team', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase.from('internal_teams').select('*').eq('team_leader_id', user.id).limit(1).maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  const { data: members = [] } = useQuery({
    queryKey: ['mkt-team-members', myTeam?.id],
    queryFn: async () => {
      if (!myTeam) return [];
      const { data: memberData } = await supabase.from('internal_team_members').select('user_id').eq('team_id', myTeam.id);
      if (!memberData?.length) return [];
      const { data: profiles } = await supabase.from('profiles').select('user_id, full_name').in('user_id', memberData.map((m: any) => m.user_id));
      return profiles || [];
    },
    enabled: !!myTeam,
  });

  const { data: stats } = useQuery({
    queryKey: ['mkt-leader-stats', myTeam?.id],
    queryFn: async () => {
      if (!myTeam) return { memberCount: 0, totalTasks: 0, campaigns: 0 };
      const [membersRes, tasksRes, campaignsRes] = await Promise.all([
        supabase.from('internal_team_members').select('*', { count: 'exact', head: true }).eq('team_id', myTeam.id),
        supabase.from('internal_tasks').select('*', { count: 'exact', head: true }).eq('team_id', myTeam.id),
        supabase.from('marketing_campaigns').select('*', { count: 'exact', head: true }).eq('team_id', myTeam.id),
      ]);
      return { memberCount: membersRes.count || 0, totalTasks: tasksRes.count || 0, campaigns: campaignsRes.count || 0 };
    },
    enabled: !!myTeam,
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Marketing Team Leader</h2>
        <p className="text-muted-foreground">{myTeam?.name || 'Your Team'}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3"><Users className="h-8 w-8 text-blue-500" /><div><p className="text-2xl font-bold">{stats?.memberCount || 0}</p><p className="text-xs text-muted-foreground">Team Members</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><ListTodo className="h-8 w-8 text-orange-500" /><div><p className="text-2xl font-bold">{stats?.totalTasks || 0}</p><p className="text-xs text-muted-foreground">Team Tasks</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><Megaphone className="h-8 w-8 text-primary" /><div><p className="text-2xl font-bold">{stats?.campaigns || 0}</p><p className="text-xs text-muted-foreground">Campaigns</p></div></CardContent></Card>
      </div>

      <InternalTaskBoard teamId={myTeam?.id} canCreate teamMembers={members} />
      <CampaignManager teamId={myTeam?.id} />
    </div>
  );
}
